import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type SelectedState } from '../API/services/PartnerFacilityFilterService';

type FilterState = {
  facilities: SelectedState;
  statusIds: number[];
  followUpIds: number[];
  roleIds: number[];
  searchedValue: string;
};

const defaultFilters: FilterState = {
  facilities: {},
  statusIds: [],
  followUpIds: [],
  roleIds: [],
  searchedValue: '',
};

const STORAGE_KEY = 'Filters';

const FilterContext = createContext<{
  filterContext: FilterState;
  setFilterContext: (filters: FilterState) => void;
  clearFilters: () => void;
  getFilterCounts: (filters: FilterState) => {
    statusCount: number;
    followUpCount: number;
    facilityCount: number;
    roleCount: number;
    totalCount: number;
  };
}>({
  filterContext: defaultFilters,
  setFilterContext: () => {},
  clearFilters: () => {},
  getFilterCounts: () => ({
    statusCount: 0,
    facilityCount: 0,
    followUpCount: 0,
    roleCount: 0,
    totalCount: 0,
  }),
});

export const FilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [filterValues, setFilterValues] = useState<FilterState>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : defaultFilters;
  });

  // persist to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filterValues));
  }, [filterValues]);

  const setFilters = (newFilters: FilterState) => {
    setFilterValues(newFilters);
  };

  const clearFilters = () => {
    setFilterValues(defaultFilters);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const getFilterCounts = (filters: FilterState) => {
    const statusCount = filters.statusIds.length;
    const followUpCount = filters.followUpIds?.length ?? 0;
    const roleCount = filters.roleIds?.length;

    const facilityCount = Object.values(filters.facilities).reduce(
      (acc, curr) => acc + curr.length,
      0
    );

    return {
      statusCount,
      followUpCount,
      roleCount,
      facilityCount,
      totalCount: statusCount + followUpCount + roleCount + facilityCount,
    };
  };
  const contextValue = useMemo(
    () => ({
      filterContext: filterValues,
      setFilterContext: setFilters,
      clearFilters,
      getFilterCounts,
    }),
    [filterValues, setFilters, clearFilters, getFilterCounts]
  );

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = () => useContext(FilterContext);
